import { numberToLetters } from '@/shared/utils/numbers.lib'
import { type StructureType, StructureTypeLabel } from '../catalogs/structure-type'
import { type UnitType, UnitTypeLabel } from '../catalogs/unit-type'
import { defineVariable, type NegotiationVariable } from '../core/define'

// Helpers de presentación del valor crudo.
const str = (v: unknown): string => (v === undefined || v === null ? '' : String(v))
const num = (v?: number): string => (v === undefined || v === null || isNaN(v) ? '' : String(v))

// "Tipo" de unidad en letras: si es numérico lo pasa a letras (3 → Tres);
// si es una letra/palabra, lo deja igual (A). Lógica especial — NO es una
// máscara letters pura, por eso es su propia variable.
const tipoToLetters = (tipo?: string): string => {
  const t = tipo?.trim() ?? ''
  if (t === '') return ''
  return /^\d+$/.test(t) ? numberToLetters(Number(t)) : t
}

/**
 * Resolución de las variables del INMUEBLE (datos físicos y registrales, desde la propiedad).
 *
 * El metadato está en `@idc-forms/schemas/variables/document/property.variables`. Nota: las
 * versiones "en letras" YA NO son variables — son la máscara `letters` aplicable a cualquier
 * variable numérica. Una variable, N presentaciones.
 */
export const PROPERTY_VARIABLES: NegotiationVariable[] = [
  defineVariable('nombre_propiedad', (c) => str(c.property?.name)),
  defineVariable('codigo_propiedad', (c) => str(c.property?.code)),
  defineVariable('tipo_propiedad', (c) => str(c.typeProperty?.code)),
  defineVariable('tipo_nombre_propiedad', (c) => str(c.typeProperty?.name)),

  // Identificación del inmueble
  defineVariable('unidad', (c) => UnitTypeLabel[c.property?.unitType as UnitType] ?? ''),
  defineVariable('unidad_tipo', (c) => str(c.property?.tipo)),
  defineVariable('unidad_tipo_letras', (c) => tipoToLetters(c.property?.tipo)),
  defineVariable(
    'estructura',
    (c) => StructureTypeLabel[c.property?.structureType as StructureType] ?? ''
  ),
  defineVariable('estructura_numero', (c) => num(c.property?.structureNumber)),
  defineVariable('estructura_codigo', (c) =>
    c.property?.structureType && c.property?.structureNumber
      ? `${c.property.structureType}${c.property.structureNumber}`
      : ''
  ),
  defineVariable('identificador', (c) => num(c.property?.identifier)),

  // Terreno y construcción (numéricas → la máscara `letters`/`lettersDecimal` da la versión en letras)
  defineVariable('terreno_m2', (c) => num(c.property?.terrainM2)),
  defineVariable('terreno_v2', (c) => num(c.property?.terrainV2)),
  defineVariable('construccion_m2', (c) => num(c.property?.constructionM2)),
  defineVariable('terraza_m2', (c) => num(c.property?.terraceM2)),
  defineVariable('parqueo_m2', (c) => num(c.property?.parkingM2)),

  // Datos registrales
  defineVariable('finca', (c) => str(c.property?.property)),
  defineVariable('folio', (c) => str(c.property?.folio)),
  defineVariable('libro', (c) => str(c.property?.book)),
  defineVariable('monto_iusi', (c) => num(c.property?.iusiAmount)),
  defineVariable('contador_agua', (c) => str(c.property?.waterMeter)),
  defineVariable('contador_electricidad', (c) => str(c.property?.electricityMeter)),
  defineVariable('telefono_propiedad', (c) => str(c.property?.telephone))
]
